import { CommonModule } from '@angular/common';
import { Component, ElementRef, afterEveryRender, computed, effect, inject, input, signal, viewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { GcmCustomMatDialogComponent, GcmMatDialogService } from '@gcm/gcm-ui-angular';
import { TourMatMenu } from 'ngx-ui-tour-md-menu';
import { DealDashboardDetails } from 'src/app/models/classes/deal-dashboard-details.class';
import { CurrencyFormatPipe } from 'src/app/pipes/currency-format.pipe';
import { CurrencySymbolPipe } from 'src/app/pipes/currency-symbol.pipe';
import { IrrValueFormatPipe } from 'src/app/pipes/irr-value-format.pipe';
import { SafeHtmlPipe } from 'src/app/pipes/safe-html.pipe';
import { TvpiValueFormatPipe } from 'src/app/pipes/tvpi-value-format.pipe';
import { ActivityTrackingService } from 'src/app/services/activity-tracking.service';
import { ContentfulService } from '../../services/contentful.service';
import { InvestmentManagerResponse } from 'src/app/models/interfaces/contentful-response.interface';
import { SponsorsContentComponent } from './sponsors-dialog/sponsors-content.component';

@Component({
    selector: 'app-deal-dashboard-summary',
    imports: [
        CommonModule,
        CurrencyFormatPipe,
        TvpiValueFormatPipe,
        IrrValueFormatPipe,
        CurrencySymbolPipe,
        SafeHtmlPipe,
        TourMatMenu
    ],
    templateUrl: './deal-dashboard-summary.component.html',
    styleUrls: ['./deal-dashboard-summary.component.scss']
})
export class DealDashboardSummaryComponent {
  private activityTracking = inject(ActivityTrackingService);
  private dialogService = inject(GcmMatDialogService);
  private dialog = inject(MatDialog);
  private contentfulService = inject(ContentfulService);

  private readonly sponsorsEffect = effect(() => this.getSponsors());

  readonly iconSet = input<Record<string, string>>();
  readonly dealTitle = input<string>();
  readonly dealSummary = input<DealDashboardDetails>();
  readonly dealSummaryText = input<string>();
  readonly showSummary = computed(() => {
    const text = this.dealSummaryText();
    return text !== '' && text !== null && text !== undefined;
  });
  showMoreBtn = signal(false);
  sponsors = signal<{ logoUrl: string; name: string }[]>([]);
  readonly displayedSponsors = computed(() => {
    const all = this.sponsors();
    return all.length > 4 ? all.slice(0, 4) : all;
  });
  readonly hasMoreSponsors = computed(() => this.sponsors().length > 4);
  readonly targetSummaryElement = viewChild<ElementRef>('summaryElement');

  constructor() {
    afterEveryRender(() => {
      const el = this.targetSummaryElement();
      if (el) {
        this.showMoreBtn.set(
          el.nativeElement.offsetHeight < el.nativeElement.scrollHeight ||
          el.nativeElement.offsetWidth < el.nativeElement.scrollWidth
        );
      }
    });
  }

  openModal() {
    const dealTitle = this.dealTitle() as string;
    this.activityTracking.viewMoreSummary((this.dealSummary() as DealDashboardDetails).masterId, dealTitle);
    this.dialogService.showInformationDialog(dealTitle, this.dealSummaryText() as string);
  }

  openSponsorsModal() {
    this.dialog.open(GcmCustomMatDialogComponent, {
      panelClass: ['responsive-dialog'],
      width: '900px',
      maxWidth: '90vw',
      autoFocus: 'dialog',
      data: {
        title: 'Investment Managers',
        sponsors: this.sponsors(),
        component: SponsorsContentComponent,
        showCloseIcon: true,
        showActionBtns: true,
        okText: 'OK'
      }
    });
  }

  getSponsors() {
    const sponsors = this.dealSummary()?.sponsors ?? [];
    this.sponsors.set([]);

    sponsors.forEach((sponsor) => {
      this.contentfulService
        .fetchCommonAssets('investmentManager', sponsor.masterId.toString())
        .subscribe((data) => {
          const response = data as InvestmentManagerResponse;
          const items = response?.data?.investmentManagerCollection?.items ?? [];

          if (items.length > 0) {
            this.sponsors.update((current) => [
              ...current,
              { logoUrl: items[0].logo.url, name: sponsor.name }
            ]);
          }
        });
    });
  }
}